import {
  Button,
  ModalBody,
  ModalCloseButton,
  ModalHeader,
  ModalContent,
  Modal,
  ModalOverlay,
  ModalFooter,
  VStack,
  Skeleton,
} from '@chakra-ui/react';
import React, { useCallback, useMemo, useState } from 'react';

import { date as commonDate } from '@finley-technologies/common';
import { Util } from '@finley-technologies/finley-types';

import { InvoiceFragmentGQLType } from 'types/dashboard-core-gqlTypes.generated';

import { useGetEarliestOriginationDateForLoansQuery } from 'packages/loans/requests/loans.gql.generated';
import { SimpleDatePicker } from '~/packages/common-frontend/components/SimpleDatePicker';
import { Typography } from '~/packages/common-frontend/components/Typography';

export const MarkInvoiceAsPaidModal = ({
  markInvoiceAsPaid,
  isMarkInvoiceAsPaidLoading,
  isOpen,
  onClose,
  invoice,
}: {
  markInvoiceAsPaid: (paymentDate: Util.FullDateStr) => Promise<void>;
  isMarkInvoiceAsPaidLoading: boolean;
  isOpen: boolean;
  onClose: () => void;
  invoice: InvoiceFragmentGQLType;
}) => {
  const clientTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const loansOnInvoice: string[] = useMemo(
    () =>
      invoice.invoiceComponentsWithInvoiceItems
        .map((component) => component.invoiceComponent.finleyLoanId ?? '')
        .filter((loanId) => loanId !== ''),
    [invoice.invoiceComponentsWithInvoiceItems],
  );

  const {
    data: earliestOriginationDateData,
    loading: isEarliestOriginationDateLoading,
    error: earliestOriginationDateError,
  } = useGetEarliestOriginationDateForLoansQuery({
    variables: {
      creditFacilityId: invoice.creditFacilityId,
      loanIds: loansOnInvoice,
    },
  });

  const maxPaymentDate = useMemo(() => {
    // only support marking as paid on the current date or before
    return new Date();
  }, []);
  const defaultPaymentDate: Util.FullDateStr = useMemo(() => {
    const invoiceDueDateJs = commonDate.fullDateStrToDate(invoice.dueDate);
    const defaultPaymentDateJs =
      invoiceDueDateJs < maxPaymentDate ? invoiceDueDateJs : maxPaymentDate;

    return commonDate.dateToFullDateString(
      defaultPaymentDateJs,
      clientTimeZone,
    );
  }, [invoice.dueDate, maxPaymentDate, clientTimeZone]);

  const [
    selectedPaymentDate,
    setSelectedPaymentDate,
  ] = useState<Util.FullDateStr>(defaultPaymentDate);

  const onCloseModal = useCallback(() => {
    onClose();
    setSelectedPaymentDate(defaultPaymentDate);
  }, [onClose, defaultPaymentDate]);

  if (earliestOriginationDateError) {
    throw earliestOriginationDateError;
  }

  if (isEarliestOriginationDateLoading) {
    return (
      <Modal isOpen={isOpen} onClose={onCloseModal} size="3xl">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Mark Invoice as Paid</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Skeleton h="200px" />
          </ModalBody>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={onCloseModal} size="3xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Mark Invoice as Paid</ModalHeader>
        <ModalCloseButton />
        <ModalBody>
          <VStack w="100%" gap="8px" alignItems="flex-start">
            <Typography fontWeight="semibold">Payment Date</Typography>
            <SimpleDatePicker
              selectedDate={commonDate.fullDateStrToDate(selectedPaymentDate)}
              setSelectedDate={(date: Date | null) => {
                // date must exist. this datepicker is not clearable
                if (date) {
                  const newPaymentDate = commonDate.dateToFullDateString(
                    date,
                    clientTimeZone,
                  );
                  setSelectedPaymentDate(newPaymentDate);
                }
              }}
              width="100%"
              dateFormat="MMMM d, yyyy"
              minDate={
                earliestOriginationDateData?.earliestOriginationDate
                  ? commonDate.fullDateStrToDate(
                      earliestOriginationDateData.earliestOriginationDate,
                    )
                  : undefined // if no loans on the invoice, no min date
              }
              maxDate={maxPaymentDate} // only support marking as paid on the current date or before
            />
          </VStack>
        </ModalBody>

        <ModalFooter mt="24px">
          <Button
            colorScheme="aurora"
            variant="outline"
            borderWidth="2px"
            mr="32px"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            colorScheme="aurora"
            color="white"
            onClick={async () => {
              await markInvoiceAsPaid(selectedPaymentDate);
            }}
            isLoading={isMarkInvoiceAsPaidLoading}
          >
            Mark as Paid
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
